import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  MenuItem,
  Alert,
  LinearProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  AddOutlined as AddIcon,
  AutoAwesomeOutlined as AutoAwesomeIcon,
  DescriptionOutlined as DescriptionIcon,
  LightbulbOutlined as LightbulbIcon
} from '@mui/icons-material';
import { TemplateCategory } from '@/types/templateCategoryTypes';
import { Template, TemplateDraft } from '@/types/templateTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { AttioTextField, AttioSelect } from '@/components/AttioForm';
import TemplateCategoryPicker from '@/components/TemplateCategoryPicker/TemplateCategoryPicker';
import {
  generateTemplateFromPrompt,
  createTemplateFromGenerated,
  EXAMPLE_PROMPTS
} from '@/services/templateGenerateService';
import {
  previewImportFromFile,
  startTemplateImportJob,
  getTemplateImportStatus
} from '@/services/templateImportService';

type CreationMethod = 'blank' | 'ai' | 'document';

interface UnifiedTemplateCreationDialogProps {
  open: boolean;
  onClose: () => void;
  categories: TemplateCategory[];
  isLoadingCategories: boolean;
  onCreated: (template: Template) => void;
  onCreate: (name: string, description: string, categoryId: number, titlePrompt?: string) => Promise<void>;
}

export const UnifiedTemplateCreationDialog: React.FC<UnifiedTemplateCreationDialogProps> = ({
  open,
  onClose,
  categories,
  isLoadingCategories,
  onCreated,
  onCreate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();
  const userId = appUser?.id;

  // Common fields
  const [method, setMethod] = useState<CreationMethod>('blank');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [titlePrompt, setTitlePrompt] = useState('');

  // AI-specific
  const [aiPrompt, setAiPrompt] = useState('');
  const [preview, setPreview] = useState<TemplateDraft | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Document-specific
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [importPercent, setImportPercent] = useState<number>(0);
  const [importStep, setImportStep] = useState<string | null>(null);

  // General
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  const resetState = () => {
    setMethod('blank');
    setCategoryId('');
    setTemplateName('');
    setDescription('');
    setTitlePrompt('');
    setAiPrompt('');
    setPreview(null);
    setFile(null);
    setJobId(null);
    setImportPercent(0);
    setImportStep(null);
    setIsGenerating(false);
    setIsCreating(false);
    setError(null);
    setStatusMessage('');
  };

  const handleClose = () => {
    if (!isGenerating && !isCreating) {
      resetState();
      onClose();
    }
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt || !userId || !categoryId) {
      setError('Please enter a prompt and select a category.');
      return;
    }
    setIsGenerating(true);
    setError(null);
    setStatusMessage('Generating template with AI...');
    try {
      const draft = await generateTemplateFromPrompt(
        aiPrompt,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        200
      );
      setPreview(draft);
      setStatusMessage('');
      if (!templateName && draft.name) {
        setTemplateName(draft.name);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to generate template');
      setStatusMessage('');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImportPreview = async () => {
    if (!file || !userId || !categoryId) {
      setError('Please select a file and a category.');
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const draft = await previewImportFromFile(
        file,
        Number(categoryId),
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        templateName || undefined
      );
      setPreview(draft);
    } catch (e: any) {
      setError(e?.message || 'Failed to preview import');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (method === 'blank') {
      // Create blank template
      if (!templateName.trim() || !categoryId) {
        setError('Please enter a template name and select a category.');
        return;
      }
      setIsCreating(true);
      try {
        await onCreate(templateName, description, categoryId as number, titlePrompt || undefined);
        resetState();
        onClose();
      } catch (e: any) {
        setError(e?.message || 'Failed to create template');
      } finally {
        setIsCreating(false);
      }
    } else if (method === 'ai' && preview) {
      // Create from AI generation
      setIsCreating(true);
      setError(null);
      setStatusMessage('Creating template...');
      try {
        const newTemplate = await createTemplateFromGenerated(
          preview,
          Number(categoryId),
          userId!,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          templateName || preview.name || undefined
        );
        setStatusMessage('');
        onCreated(newTemplate);
        resetState();
        onClose();
      } catch (e: any) {
        setError(e?.message || 'Failed to create template');
        setStatusMessage('');
      } finally {
        setIsCreating(false);
      }
    } else if (method === 'document' && file) {
      // Start document import job
      setIsCreating(true);
      setError(null);
      try {
        const { jobId: jid } = await startTemplateImportJob(
          file,
          Number(categoryId),
          userId!,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          templateName || undefined
        );
        setJobId(jid);
        // Poll for status (simplified - you may want to add the full polling logic)
        // For now, just show that it started
        setStatusMessage('Import started...');
      } catch (e: any) {
        setError(e?.message || 'Failed to start import');
      } finally {
        setIsCreating(false);
      }
    }
  };

  const loadExamplePrompt = (key: keyof typeof EXAMPLE_PROMPTS) => {
    setAiPrompt(EXAMPLE_PROMPTS[key]);
    setPreview(null);
    setError(null);
  };

  const isLoading = isGenerating || isCreating;

  const canCreate = () => {
    if (!categoryId) return false;
    if (method === 'blank') return templateName.trim() !== '';
    if (method === 'ai') return !!preview;
    if (method === 'document') return !!file;
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.6)'
            : '0 8px 32px rgba(0, 0, 0, 0.12)',
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: isMobile ? 2 : 3,
          py: isMobile ? 1.5 : 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          Create New Template
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            borderRadius: '6px',
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: isMobile ? 2 : 3, pt: isMobile ? 2.5 : 4, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 2.5, pt: isMobile ? 0.5 : 1.5 }}>
          {error && <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>}

          {/* Creation Method Selector */}
          <Box>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                mb: 1,
                display: 'block'
              }}
            >
              Creation Method
            </Typography>
            <ToggleButtonGroup
              value={method}
              exclusive
              onChange={(e, newMethod) => {
                if (newMethod !== null) {
                  setMethod(newMethod);
                  setPreview(null);
                  setError(null);
                }
              }}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: isMobile ? '13px' : '14px',
                  py: isMobile ? 1.5 : 1.25,
                  border: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
                    color: theme.palette.mode === 'dark' ? '#60a5fa' : '#2563eb',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#93c5fd',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : '#bfdbfe'
                    }
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                },
                '& .MuiToggleButtonGroup-grouped': {
                  '&:not(:first-of-type)': {
                    marginLeft: isMobile ? 0 : '8px',
                    marginTop: isMobile ? '8px' : 0,
                    borderLeft: isMobile ? `1px solid ${theme.palette.divider}` : `1px solid ${theme.palette.divider}`
                  },
                  '&:not(:last-of-type)': {
                    borderRight: `1px solid ${theme.palette.divider}`
                  }
                }
              }}
            >
              <ToggleButton value="blank">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isMobile ? 'flex-start' : 'center', width: '100%' }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                  Blank Template
                </Box>
              </ToggleButton>
              <ToggleButton value="ai">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isMobile ? 'flex-start' : 'center', width: '100%' }}>
                  <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  Generate with AI
                </Box>
              </ToggleButton>
              <ToggleButton value="document">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isMobile ? 'flex-start' : 'center', width: '100%' }}>
                  <DescriptionIcon sx={{ fontSize: 18 }} />
                  Import from File
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Common Fields */}
          <Box
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: theme.palette.background.paper,
                padding: 0,
                '& fieldset': {
                  borderColor: theme.palette.divider
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '1px'
                }
              },
              '& .MuiInputBase-input': {
                padding: '8px 12px',
                fontSize: '14px',
                fontWeight: 400,
                textAlign: 'left'
              },
              '& .MuiInputLabel-root': {
                display: 'none'
              }
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                mb: 0.75,
                display: 'block'
              }}
            >
              Template Category *
            </Typography>
            <TemplateCategoryPicker
              value={categoryId}
              onChange={(e) => {
                const val = e.target.value;
                setCategoryId(val === '' ? '' : Number(val));
              }}
              categories={categories}
              isLoadingCategories={isLoadingCategories}
              disabled={isLoading}
              required={true}
              fullWidth
              variant="outlined"
              labelId="unified-template-category-label"
              id="unified-template-category-select"
              label=""
            />
          </Box>

          <AttioTextField
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            disabled={isLoading}
            required={method === 'blank'}
            placeholder={method === 'blank' ? 'Enter template name' : 'Leave blank for AI to generate'}
            description={method !== 'blank' ? 'AI will generate a name if left empty' : undefined}
          />

          {method === 'blank' && (
            <>
              <AttioTextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isLoading}
                multiline
                minRows={isMobile ? 2 : 2}
                maxRows={isMobile ? 3 : 4}
                placeholder="Describe the purpose of this template..."
              />

              <AttioTextField
                label="AI Title Generation Prompt (Optional)"
                value={titlePrompt}
                onChange={(e) => setTitlePrompt(e.target.value)}
                disabled={isLoading}
                multiline
                minRows={isMobile ? 2 : 2}
                maxRows={isMobile ? 4 : 6}
                placeholder="Custom prompt for AI to generate session titles..."
                description="Leave blank to use default prompt"
              />
            </>
          )}

          {method === 'ai' && (
            <>
              {!preview && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                    <LightbulbIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: theme.palette.text.secondary
                      }}
                    >
                      Quick Examples
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                    {[
                      { key: 'patientIntake', label: 'Patient Intake' },
                      { key: 'employeeOnboarding', label: 'Employee Onboarding' },
                      { key: 'customerFeedback', label: 'Customer Feedback' },
                      { key: 'eventRegistration', label: 'Event Registration' },
                      { key: 'jobApplication', label: 'Job Application' }
                    ].map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        onClick={() => loadExamplePrompt(key as keyof typeof EXAMPLE_PROMPTS)}
                        clickable
                        sx={{
                          height: isMobile ? '32px' : '28px',
                          fontSize: isMobile ? '13px' : '12px',
                          fontWeight: 500,
                          borderRadius: '6px',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                          border: `1px solid ${theme.palette.divider}`,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb',
                            borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <AttioTextField
                label="Describe Your Template"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                disabled={isLoading}
                multiline
                minRows={isMobile ? 3 : 4}
                maxRows={isMobile ? 6 : 10}
                required
                placeholder="E.g., 'Create a patient intake form for a medical clinic with questions about medical history, allergies, medications, and emergency contacts'"
                description="Describe what kind of template you need and what questions it should include"
              />

              {!preview && (
                <Button
                  variant="outlined"
                  onClick={handleGenerateAI}
                  disabled={!aiPrompt || !categoryId || isLoading}
                  fullWidth={isMobile}
                  startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                    py: isMobile ? 1.5 : 1.25,
                    borderColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#2563eb',
                    color: theme.palette.mode === 'dark' ? '#60a5fa' : '#2563eb',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(37, 99, 235, 0.05)',
                      borderColor: theme.palette.mode === 'dark' ? '#60a5fa' : '#1d4ed8'
                    },
                    '&:disabled': {
                      opacity: 0.5
                    }
                  }}
                >
                  {isGenerating ? 'Generating...' : 'Generate Template'}
                </Button>
              )}

              {preview && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px'
                    }}
                  >
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      borderRadius: '8px',
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#fafafa'
                    }}
                  >
                    <Typography sx={{ fontSize: isMobile ? '13px' : '14px', fontWeight: 600, mb: 0.5 }}>
                      {preview.name}
                    </Typography>
                    <Typography sx={{ fontSize: isMobile ? '11px' : '12px', color: 'text.secondary', mb: 1.5 }}>
                      {preview.description}
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {preview.questions?.slice(0, 5).map((q, idx) => (
                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`${idx + 1}. ${q.title}`}
                            primaryTypographyProps={{
                              sx: { fontSize: '13px', color: 'text.primary' }
                            }}
                          />
                        </ListItem>
                      ))}
                      {preview.questions && preview.questions.length > 5 && (
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`...and ${preview.questions.length - 5} more`}
                            primaryTypographyProps={{
                              sx: { fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                </Box>
              )}
            </>
          )}

          {method === 'document' && (
            <>
              <Box>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    mb: 1
                  }}
                >
                  Document File *
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={isLoading || !!jobId}
                  sx={{
                    width: '100%',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    py: 1.25,
                    borderStyle: 'dashed',
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    justifyContent: 'center',
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                    }
                  }}
                >
                  {file ? `Selected: ${file.name}` : 'Choose PDF/DOCX/TXT file'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </Button>
              </Box>

              {file && !preview && (
                <Button
                  variant="outlined"
                  onClick={handleImportPreview}
                  disabled={!file || !categoryId || isLoading}
                  startIcon={isGenerating ? <CircularProgress size={16} /> : <DescriptionIcon />}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                    py: 1.25,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
                    }
                  }}
                >
                  {isGenerating ? 'Processing...' : 'Preview Template'}
                </Button>
              )}

              {preview && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px'
                    }}
                  >
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '8px',
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#fafafa'
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
                      {preview.name || 'Imported Template'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1.5 }}>
                      {preview.description || 'No description'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Questions ({preview.questions?.length || 0})
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {preview.questions?.slice(0, 5).map((q, idx) => (
                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`${idx + 1}. ${q.title}`}
                            primaryTypographyProps={{
                              sx: { fontSize: '13px', color: 'text.primary' }
                            }}
                          />
                        </ListItem>
                      ))}
                      {preview.questions && preview.questions.length > 5 && (
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemText
                            primary={`...and ${preview.questions.length - 5} more`}
                            primaryTypographyProps={{
                              sx: { fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                </Box>
              )}

              {jobId && (
                <Box>
                  <LinearProgress variant="determinate" value={importPercent} sx={{ borderRadius: '4px' }} />
                  <Typography variant="body2" align="center" sx={{ mt: 1, fontSize: '13px', color: 'text.secondary' }}>
                    {importStep || 'Processing'} ({importPercent}%)
                  </Typography>
                </Box>
              )}
            </>
          )}

          {statusMessage && (
            <Alert severity="info" sx={{ borderRadius: '8px', fontSize: '13px' }}>
              {statusMessage}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: isMobile ? 2 : 3, 
          pb: isMobile ? 2 : 3, 
          pt: 2,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: isMobile ? 1 : 0
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isLoading}
          fullWidth={isMobile}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
            px: 2.5,
            py: isMobile ? 1.25 : 0.75,
            color: theme.palette.text.secondary
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!canCreate() || isLoading}
          fullWidth={isMobile}
          startIcon={isCreating ? <CircularProgress size={16} /> : undefined}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            py: isMobile ? 1.25 : 0.75,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            }
          }}
        >
          {isCreating ? 'Creating...' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedTemplateCreationDialog;

